import type { ReactElement } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { WorkflowView } from '@/lib/api/generated/model';

import {
  PENDING_CHANGES_LABEL,
  draftMarkerTooltip,
  pendingChangesSaved,
} from '../builder/draftCopy';

/**
 * "Unpublished changes" beside a published workflow that has them (spec 144
 * FR-010), with who saved them and when.
 *
 * A shared draft nobody can see is one nobody publishes, so the Workflows list
 * shows it without opening the workflow. The board does not: what runs there
 * is the published version, and a marker would suggest otherwise. Nor does the
 * sidebar (spec 175), which lists it as the board it is.
 *
 * Not "Draft", the never-published workflow's word, which it used to share
 * (spec 175): on the Workflows page it sat beside a count of "0 drafts". The
 * saver and time are in the tooltip for a pointer, and read out with the
 * phrase for everyone else.
 *
 * `render` is the element that carries it, so each list keeps its own look.
 */
export function PendingChangesMarker({
  workflow,
  render,
}: {
  workflow: WorkflowView;
  render: ReactElement;
}) {
  if (workflow.status !== 'published' || !workflow.has_pending_changes) {
    return null;
  }

  const detail = draftMarkerTooltip(
    workflow.pending_saved_by,
    workflow.pending_saved_at,
  );
  const saved = pendingChangesSaved(
    workflow.pending_saved_by,
    workflow.pending_saved_at,
  );
  return (
    <Tooltip>
      <TooltipTrigger render={render}>
        {PENDING_CHANGES_LABEL}
        <span className="sr-only"> ({saved})</span>
      </TooltipTrigger>
      <TooltipContent>{detail}</TooltipContent>
    </Tooltip>
  );
}
